import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { paddingTop } from 'html2canvas/dist/types/css/property-descriptors/padding';
import Button from '../../../../components/Button';
import FormInput from '../../../../components/FormInput';
import FormSwitch from '../../../../components/FormSwitch';
import LoadingState from '../../../../components/LoadingState';
import getActiveLanguage from '../../../../helpers/getActiveLanguage';
import useUtilities from '../../../../hooks/useUtilities';
import styles from '../../styles';
import {
  RegisterSecondStepFormValues,
  RegisterSecondStepFormValidValues,
  validationSchema,
  defaultValues,
} from './types';

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  paddingBottom: '0px',
  marginBottom: '0px',
  position: 'relative' as 'relative',
};

const inputStyle: React.CSSProperties = {
  top: '0',
  bottom: '0',
  marginBottom: '10px',
  paddingTop: '16px',
  left: '0',
  width: '100%',
  padding: '16.5px 14px',
  fontSize: '16px',
  height: '25px',
  border: 'none',
  borderBottom: '1px solid #000',
  borderBottomColor: '#000',
  borderRadius: '0',
  backgroundColor: 'rgb(245, 248, 250)',
  transition: 'border-color 0.2s ease-in-out',
  outline: 'none',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
};

const inputFocusStyle: React.CSSProperties = {
  borderBottom: '2px solid #000', // Fokus olunca da aynı siyah çizgi
};

const defaultCenter = {
  lat: 41.0082,
  lng: 28.9784,
};

interface Props {
  onPrevClick: () => void;
  onSubmitClick: (values: RegisterSecondStepFormValidValues) => void;
  isLast: boolean;
  isFirst: boolean;
  loading: boolean;
  initialValues: RegisterSecondStepFormValues | null;
}

function SecondStepForm({
  onPrevClick,
  onSubmitClick,
  isLast,
  isFirst,
  loading,
  initialValues,
}: Props) {
  const { t } = useTranslation('companyRegister');
  const { handleSubmit, control, setValue, reset } =
    useForm<RegisterSecondStepFormValues>({
      defaultValues,
      resolver: validationSchema,
    });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries: ['places'],
  });

  const {
    paymentTypes,
    paymentTypesLoading,
    fetchBusinessTypes,
    fetchWorkingTypes,
    fetchCurrencies,
    fetchPaymentTypes,
  } = useUtilities();

  const [center, setCenter] = useState(defaultCenter);
  const inputRef = useRef<HTMLInputElement>(null);

  const onSubmit = handleSubmit(data => {
    const paymentTypesValues = data.paymentTypes.map((active, index) => ({
      id: paymentTypes[index].id,
      active,
    }));
    onSubmitClick({
      name: data.name,
      address: data.address,
      lat: data.lat,
      lng: data.lng,
      paymentTypes: paymentTypesValues,
    });
  });

  const initializeAutocomplete = () => {
    if (inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setCenter({ lat, lng });
          setValue('lat', lat);
          setValue('lng', lng);
          if (inputRef.current && place.formatted_address) {
            inputRef.current.value = place.formatted_address;
            setValue('address', place.formatted_address);
          }
        }
      });
    }
  };
  const fetchAddress = async (lat: number, lng: number): Promise<string> => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`,
      );
      const data = await response.json();
      if (data.results && data.results[0]) {
        return data.results[0].formatted_address;
      }
    } catch (error) {
      console.error('Error fetching address:', error);
    }
    return '';
  };
  const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();
    if (lat && lng) {
      setCenter({ lat, lng });
      setValue('lat', lat);
      setValue('lng', lng);
      const address = await fetchAddress(lat, lng);
      setValue('address', address);
      if (inputRef.current) {
        inputRef.current.value = address;
      }
    }
  };
  const setDefaultPaymentType = () => {
    const defaultTypeIndex = paymentTypes.findIndex(item => item.is_default);
    if (defaultTypeIndex > -1) {
      setValue(`paymentTypes.${defaultTypeIndex}`, true);
    }
  };

  useEffect(() => {
    fetchCurrencies();
    fetchWorkingTypes();
    fetchBusinessTypes();
    fetchPaymentTypes();
  }, []);

  useEffect(() => {
    setDefaultPaymentType();
  }, [paymentTypes]);

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues]);

  useEffect(() => {
    if (isLoaded) {
      initializeAutocomplete();
    }
  }, [isLoaded]);

  if (loading) {
    return <LoadingState />;
  }

  return (
    <form onSubmit={onSubmit}>
      <Stack sx={styles.vStack} justifyContent="space-between">
        <Stack sx={styles.fieldsContainer} spacing={2}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <FormInput
                label={t('company.name.label')}
                error={fieldState.invalid}
                helperText={
                  fieldState.error
                    ? t(fieldState.error.message as any)
                    : undefined
                }
                {...field}
              />
            )}
          />

          {/* Google Maps Search Input and Map */}
          <div style={mapContainerStyle}>
            <div>Adres</div>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <input
                  id="origin_address_search"
                  type="text"
                  placeholder="Adres"
                  style={inputStyle}
                  onFocus={e => {
                    const target = e.target as HTMLInputElement;
                    target.style.borderBottomColor = '#4CAF50';
                    target.style.borderBottom = '1px solid';
                  }}
                  onChange={e => {
                    field.onChange(e); // Update the form field
                    if (inputRef.current) {
                      inputRef.current.value = e.target.value; // Update the inputRef
                    }
                  }}
                  ref={inputRef}
                />
              )}
            />

            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '78%' }}
                center={center}
                zoom={15}
                options={{
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker
                  position={center}
                  draggable
                  onDragEnd={handleMarkerDragEnd}
                />
              </GoogleMap>
            ) : (
              <div>loadingMap</div>
            )}
          </div>

          <Box sx={{ paddingTop: '0px', marginTop: '0px' }}>
            <Typography
              sx={{ paddingTop: '0px', marginTop: '0px' }}
              variant="subtitle1"
            >
              {t('company.paymentType.label')}
            </Typography>
            {paymentTypesLoading ? (
              <LoadingState />
            ) : (
              <Grid container gap={2}>
                {paymentTypes.map((type, index) => (
                  <Grid item key={type.id}>
                    <Controller
                      name={`paymentTypes.${index}`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormSwitch
                          label={getActiveLanguage(type.translations).name}
                          error={fieldState.invalid}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Stack>
        <Stack
          sx={{ paddingTop: '20px' }}
          direction="row"
          justifyContent="space-between"
        >
          <Button onClick={onPrevClick} disabled={isFirst}>
            {t('buttons.prev')}
          </Button>
          <Button type="submit" variant="contained" loading={loading}>
            {isLast ? t('buttons.complete') : t('buttons.next')}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}

export default SecondStepForm;
